import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { ApiResponse } from '../shared/interfaces/api-response.interface';
import { AuthResponseData, LoginPayload, RegisterPayload, User } from '../shared/interfaces/auth.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'invosoft_token';
  private readonly ROLE_KEY = 'invosoft_role';
  private readonly USER_KEY = 'invosoft_user';

  private http = inject(HttpClient);
  private router = inject(Router);

  register(payload: RegisterPayload): Observable<ApiResponse<AuthResponseData>> {
    return this.http.post<ApiResponse<AuthResponseData>>('/api/auth/register', payload).pipe(
      tap(res => this.handleAuthResponse(res))
    );
  }

  login(email: string, password: string): Observable<ApiResponse<AuthResponseData>> {
    const payload: LoginPayload = { email, password };
    return this.http.post<ApiResponse<AuthResponseData>>('/api/auth/login', payload).pipe(
      tap(res => this.handleAuthResponse(res))
    );
  }

  private handleAuthResponse(res: ApiResponse<AuthResponseData>): void {
    if (res.success) {
      sessionStorage.setItem(this.TOKEN_KEY, res.data.token);
      sessionStorage.setItem(this.ROLE_KEY, res.data.user.role);
      sessionStorage.setItem(this.USER_KEY, JSON.stringify(res.data.user));
    }
  }

  logout(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.ROLE_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    this.router.navigate(['/login']);
  }

  getUser(): User | null {
    const user = sessionStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  isLoggedIn(): boolean {
    return !!sessionStorage.getItem(this.TOKEN_KEY);
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  getRole(): string | null {
    return sessionStorage.getItem(this.ROLE_KEY);
  }

  isAdmin(): boolean {
    // In this app, 'owner' and 'accountant' might have different admin-like privileges.
    // The previous implementation checked for 'admin', but docs say 'owner', 'staff', 'accountant'.
    // Typically, 'owner' is the super-admin.
    const role = this.getRole();
    return role === 'owner' || role === 'accountant';
  }

  isOwner(): boolean {
    return this.getRole() === 'owner';
  }
}
